import { parseFrontmatter, createImageResolver } from './markdown';

export interface BlogPost {
  slug: string;
  title: string;
  date: string;
  order?: number;
  author: string;
  authorRole?: string;
  excerpt: string;
  image?: string;
  tags: string[];
  related?: string[];
  content: string;
}

// Load all markdown files from content/blog at build time
const blogFiles = import.meta.glob('../../content/blog/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
});

// Load all images from content/blog/assets at build time
const blogImages = import.meta.glob('../../content/blog/assets/*.{png,jpg,jpeg,webp,gif,svg}', {
  import: 'default',
  eager: true,
}) as Record<string, string>;

export const getImageUrl = createImageResolver(blogImages);

function parseBlogPosts(): BlogPost[] {
  const posts: BlogPost[] = [];

  for (const [path, rawContent] of Object.entries(blogFiles)) {
    // Extract slug from filename
    const filename = path.split('/').pop();
    if (!filename) continue;
    const slug = filename.replace('.md', '');

    // Parse frontmatter and content
    const { data, content } = parseFrontmatter(rawContent as string);

    posts.push({
      slug,
      title: (data.title as string) || slug,
      date: data.date instanceof Date
        ? data.date.toISOString().split('T')[0]
        : String(data.date || ''),
      order: data.order != null ? Number(data.order) : undefined,
      author: (data.author as string) || 'Neumind Team',
      authorRole: data.authorRole as string | undefined,
      excerpt: (data.excerpt as string) || '',
      image: getImageUrl(data.image as string | undefined),
      tags: Array.isArray(data.tags) ? data.tags : [],
      related: Array.isArray(data.related) ? data.related : undefined,
      content: content,
    });
  }

  return posts;
}

export const blogPosts: BlogPost[] = parseBlogPosts();

export function getBlogPost(slug: string): BlogPost | undefined {
  return blogPosts.find((post) => post.slug === slug);
}

export function getBlogPosts(): BlogPost[] {
  return [...blogPosts].sort((a, b) => {
    // Articles with order come first (lowest number first)
    if (a.order != null && b.order != null) return a.order - b.order;
    if (a.order != null) return -1;
    if (b.order != null) return 1;
    // Fallback to date (newest first)
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });
}

export function getBlogPostsByTag(tag: string): BlogPost[] {
  return getBlogPosts().filter((post) =>
    post.tags.some((t) => t.toLowerCase() === tag.toLowerCase())
  );
}

